import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { User, Lock, LogInIcon as LoginIcon, Eye, EyeOff } from "lucide-react"
import axiosInstance from "../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../constants/config"
import { trackLogin, trackButtonClick, trackFormSubmission } from "../SEO/GoogleAnalytics"
import { setStoredAuth, setStoredRefreshToken } from "../../utils/authStorage"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import { Button } from "../ui/button"
import { Label } from "../ui/label"
import { Checkbox } from "../ui/checkbox"
import { Separator } from "../ui/separator"

function Login({ updateAuthState }) {
  const [usernameOrDomain, setUsernameOrDomain] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [keepLoggedIn, setKeepLoggedIn] = useState(true)
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    // Track login attempt
    trackButtonClick('login_submit', 'guest', 'login_page')

    try {
      let response, token, refreshToken, user
      response = await axiosInstance.post(API_ENDPOINTS.authLogin, {
        usernameOrDomain: usernameOrDomain.toLowerCase().trim(),
        password: password.trim(),
        rememberMe: keepLoggedIn,
      })
      token = response.data.token
      refreshToken = response.data.refreshToken
      user = response.data.user
      if (!user || !token || !refreshToken) throw new Error("Invalid response from server")

      setStoredRefreshToken(refreshToken)
      
      // Track successful login with role
      trackLogin(user.role, 'email')
      trackFormSubmission('login_form', user.role, true)
      
      if (user.role === "vehicle") {
        setStoredAuth({ token, vehicle: { ...user }, keepLoggedIn })
        updateAuthState(token, { ...user })
        navigate("/vehicle-dashboard")
      } else {
        setStoredAuth({ token, user, keepLoggedIn })
        updateAuthState(token, user)
        switch (user.role.toLowerCase()) {
          case "superadmin":
            navigate("/dashboard")
            break
          case "superchooladmin":
            navigate("/dashboard")
            break
          case "schooladmin":
            navigate("/dashboard")
            break
          case "teacher":
          case "student":
            navigate("/dashboard")
            break
          case "staff":
            navigate(user.permissions?.length ? "/schooladmin" : "/dashboard")
            break
          default:
            console.warn("Unknown role:", user.role)
            navigate("/")
        }
      }
    } catch (error) {
      // Track failed login
      trackFormSubmission('login_form', 'guest', false)
      setError(error.response?.data?.message || "An error occurred during login")
    } finally {
      setLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="w-full min-h-[calc(100vh-64px)] flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="hidden lg:block">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-2xl">C</span>
              </div>
              <span className="text-3xl font-bold text-gray-900">Clynox</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Welcome back to Clynox</h1>
            <p className="text-xl text-gray-600 mb-8">
  Sign in to your dashboard and manage everything from one centralized platform.
</p>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <Lock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Secure Access</div>
                  <div className="text-gray-600">Your data is protected with enterprise-grade security</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">24/7 Support</div>
                  <div className="text-gray-600">Get help whenever you need it from our support team</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <Card className="border-2">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl">Sign in to your account</CardTitle>
                <CardDescription>Enter your credentials to access your dashboard</CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-lg text-center border border-red-200 animate-pulse">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="usernameOrDomain">Username or Domain</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        id="usernameOrDomain"
                        type="text"
                        placeholder="Enter your username or domain"
                        value={usernameOrDomain}
                        onChange={(e) => setUsernameOrDomain(e.target.value)}
                        required
                        className="pl-10"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="pl-10 pr-10"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember"
                        checked={keepLoggedIn}
                        onCheckedChange={(checked) => setKeepLoggedIn(Boolean(checked))}
                      />
                      <label htmlFor="remember" className="text-sm text-gray-700 cursor-pointer">
                        Remember me
                      </label>
                    </div>
                    <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700">
                      Forgot password?
                    </Link>
                  </div>

                  <Button size="lg" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} type="submit">
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <LoginIcon size={18} className="mr-2" />
                        Sign In
                      </>
                    )}
                  </Button>
                </form>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login